import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Compass, FolderOpen, ListTodo, Palette, Network, Clock, Calendar as CalendarIcon, Users, CreditCard as Edit2, Save, Trash2, Crown, Eye, Pencil, CircleUser as UserCircle, CircleCheck as CheckCircle2, Circle, Plus, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { projectService, type Project, type ProjectMember, type ProjectStage } from '@/lib/projectService';
import { useWorkspace } from '@/context/WorkspaceContext';

import { toast } from 'sonner';

import { DateRangePicker } from '@/components/ui/date-range-picker';
import { parseISO, format } from 'date-fns';
import { ProjectFileTab } from '@/components/projects/ProjectFileTab';
import { ProjectTaskTab } from '@/components/projects/ProjectTaskTab';

type TabId = 'overview' | 'discovery' | 'files' | 'tasks' | 'brand' | 'sitemap';

const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <Eye className="w-4 h-4" /> },
  { id: 'discovery', label: 'Discovery', icon: <Compass className="w-4 h-4" /> },
  { id: 'files', label: 'Files', icon: <FolderOpen className="w-4 h-4" /> },
  { id: 'tasks', label: 'Tasks', icon: <ListTodo className="w-4 h-4" /> },
  { id: 'brand', label: 'Brand', icon: <Palette className="w-4 h-4" /> },
  { id: 'sitemap', label: 'Sitemap', icon: <Network className="w-4 h-4" /> },
];

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  active: { label: 'Active', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-500/20' },
  completed: { label: 'Completed', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-500/20' },
  on_hold: { label: 'On Hold', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-500/20' },
  cancelled: { label: 'Cancelled', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-500/20' },
};



const ProjectDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { currentWorkspace, isClient } = useWorkspace();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [isEditing, setIsEditing] = useState(false);

  // Edit state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState('active');
  const [editProgress, setEditProgress] = useState(0);
  const [editStages, setEditStages] = useState<ProjectStage[]>([]);

  const loadProject = useCallback(async () => {
    if (!currentWorkspace || !slug) return;
    try {
      setLoading(true);
      const data = await projectService.getProjectBySlug(currentWorkspace.id, slug);
      setProject(data);
      if (data) {
        setEditName(data.name);
        setEditDescription(data.description);
        setEditStatus(data.status);
        setEditProgress(data.progress || 0);
        setEditStages(data.stages || []);
      }
    } catch (error) {
      console.error('Error loading project:', error);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id, slug]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const handleSave = async () => {
    if (!project) return;
    try {
      await projectService.updateProject(project.id, {
        name: editName,
        description: editDescription,
        status: editStatus as any,
        progress: editProgress,
        stages: editStages,
      });
      toast.success('Project updated');
      setIsEditing(false);
      loadProject();
    } catch {
      toast.error('Failed to update project');
    }
  };

  const handleToggleStage = async (stageId: string) => {
    if (!project || !project.stages) return;

    const updatedStages = project.stages.map(s =>
      s.id === stageId ? { ...s, is_completed: !s.is_completed } : s
    );

    const newProgress = updatedStages.length > 0
      ? Math.round((updatedStages.filter(s => s.is_completed).length / updatedStages.length) * 100)
      : 0;

    // Optimistic update — no reload needed
    setProject(prev => prev ? { ...prev, stages: updatedStages, progress: newProgress } : prev);

    try {
      await projectService.updateProject(project.id, {
        stages: updatedStages,
        progress: newProgress
      } as any);
    } catch {
      // Revert on failure
      setProject(prev => prev ? { ...prev, stages: project.stages, progress: project.progress } : prev);
      toast.error('Failed to update milestone');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex flex-col animate-pulse">
        <div className="h-48 md:h-56 bg-muted relative overflow-hidden flex flex-col justify-end">
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
          <div className="relative px-6 md:px-10 pb-6 pt-10">
            <div className="h-10 md:h-14 w-64 md:w-96 bg-muted-foreground/20 rounded-xl" />
          </div>
        </div>

        <div className="border-b border-border bg-card">
          <div className="px-6 md:px-10">
            <div className="flex gap-6 py-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-6 w-24 bg-muted rounded-md" />
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 md:px-10 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <div className="h-64 bg-card border border-border rounded-2xl" />
              <div className="h-48 bg-card border border-border rounded-2xl" />
            </div>
            <div className="space-y-8">
              <div className="h-96 bg-card border border-border rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h1 className="text-2xl font-bold text-foreground mb-2">Project not found</h1>
        <Link to="/boards/projects" className="text-workspace-accent hover:underline">← Back to Projects</Link>
      </div>
    );
  }

  const status = statusConfig[project.status] || statusConfig.active;
  const canEdit = !isClient;

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{project.name} | Projects | AgencyPlus</title>
        <meta name="description" content={project.description || `Project: ${project.name}`} />
      </Helmet>

      {/* Header Banner */}
      <div
        className="h-48 md:h-56 relative overflow-hidden flex flex-col justify-end"
        style={{
          backgroundImage: project.cover_image
            ? `url(${project.cover_image})`
            : 'linear-gradient(135deg, var(--workspace-accent, #3b82f6) 0%, var(--workspace-accent-hover, #2563eb) 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Decorative pattern overlay */}
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        
        {/* Sophisticated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />
        
        <div className="relative px-6 md:px-10 pb-6 pt-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-tight select-none drop-shadow-sm">
                  {project.name}
                </h1>
                <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-black shadow-sm h-fit mt-1 lg:mt-3 ${status.bg} ${status.color}`}>
                  <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${status.color.replace('text-', 'bg-')}`} />
                  {status.label}
                </div>
              </div>
            </div>
            
            {/* Actions Moved to Tab Bar below */}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border bg-card sticky top-0 z-10">
        <div className="px-6 md:px-10">
          <div className="flex items-center justify-between">
            <nav className="flex gap-1 overflow-x-auto no-scrollbar">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                    activeTab === tab.id
                      ? 'border-workspace-accent text-workspace-accent'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </nav>

            {canEdit && (
              <div className="flex items-center ml-4">
                {!isEditing ? (
                  <Button 
                    variant="outline"
                    size="sm" 
                    onClick={() => setIsEditing(true)}
                    className="rounded-xl h-9 px-5 border-border/60 font-semibold hover:bg-muted/50"
                  >
                    <Edit2 className="w-3.5 h-3.5 mr-2" /> Edit Project
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setIsEditing(false)} 
                      className="h-9 px-4"
                    >
                      Cancel
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={handleSave} 
                      className="h-9 bg-workspace-accent text-white hover:bg-workspace-accent-hover px-6 font-bold shadow-lg rounded-xl"
                    >
                      <Save className="w-3.5 h-3.5 mr-2" /> Save Changes
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-6 md:px-10 py-8">
        {activeTab === 'overview' && (
          <OverviewTab
            project={project}
            isEditing={isEditing}
            isClient={canEdit === false}
            editName={editName}
            editStatus={editStatus}
            editProgress={editProgress}
            editStages={editStages}
            onNameChange={setEditName}
            onStatusChange={setEditStatus}
            onProgressChange={setEditProgress}
            onStagesChange={setEditStages}
            onToggleStage={handleToggleStage}
          />
        )}

        {activeTab === 'discovery' && (
          <PlaceholderTab
            icon={<Compass className="w-12 h-12 text-workspace-accent" />}
            title="Discovery"
            description="Discovery and project research content will appear here."
          />
        )}

        {activeTab === 'files' && (
          <div className="-mx-6 md:-mx-10 -my-8">
            <ProjectFileTab
              projectId={project.id}
              projectName={project.name}
              isReadOnly={isClient && !canEdit}
              isGlobalDarkMode={false}
            />
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="-mx-6 md:-mx-10 -my-8">
            <ProjectTaskTab
              projectId={project.id}
              projectName={project.name}
              taskBoardId={project.task_board_id}
            />
          </div>
        )}

        {activeTab === 'brand' && (
          <PlaceholderTab
            icon={<Palette className="w-12 h-12 text-workspace-accent" />}
            title="Brand Assets"
            description={project.brand_id
              ? `Brand "${project.brand?.name || 'Selected'}" is linked to this project`
              : 'No brand associated with this project yet. Select one from project settings.'}
          />
        )}

        {activeTab === 'sitemap' && (
          <PlaceholderTab
            icon={<Network className="w-12 h-12 text-workspace-accent" />}
            title="Sitemap"
            description="Visual sitemap for this project"
          />
        )}
      </div>
    </div>
  );
};

// Overview Tab - Full project overview
const OverviewTab: React.FC<{
  project: Project;
  isEditing: boolean;
  isClient: boolean;
  editName: string;
  editStatus: string;
  editProgress: number;
  editStages: ProjectStage[];
  onNameChange: (v: string) => void;
  onStatusChange: (v: string) => void;
  onProgressChange: (v: number) => void;
  onStagesChange: (v: ProjectStage[]) => void;
  onToggleStage: (id: string) => void;
}> = ({ 
  project, isEditing, isClient, editName, editStatus, editProgress, editStages,
  onNameChange, onStatusChange, onProgressChange, onStagesChange, onToggleStage,
}) => {
  const [newStage, setNewStage] = useState('');
  const [newStageStart, setNewStageStart] = useState('');
  const [newStageEnd, setNewStageEnd] = useState('');
  const [zoom, setZoom] = useState<'7d' | '1m' | '3m' | '6m'>('1m');

  const addStage = () => {
    if (!newStage.trim()) return;
    const stage: ProjectStage = {
      id: Math.random().toString(36).substring(2, 11),
      name: newStage.trim(),
      is_completed: false,
      start_date: newStageStart || undefined,
      end_date: newStageEnd || undefined
    };
    onStagesChange([...editStages, stage]);
    setNewStage('');
    setNewStageStart('');
    setNewStageEnd('');
  };

  const moveStage = (idx: number, direction: 'up' | 'down') => {
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= editStages.length) return;
    const updated = [...editStages];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    onStagesChange(updated);
  };

  const removeStage = (id: string) => {
    onStagesChange(editStages.filter(s => s.id !== id));
  };

  // Auto-calculate progress based on stages in edit mode
  useEffect(() => {
    if (isEditing && editStages.length > 0) {
      const completed = editStages.filter(s => s.is_completed).length;
      const progress = Math.round((completed / editStages.length) * 100);
      if (progress !== editProgress) {
        onProgressChange(progress);
      }
    }
  }, [editStages, isEditing, editProgress, onProgressChange]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      {/* Main Content */}
      <div className="lg:col-span-9 space-y-8">
        {/* Card 1: Project Progress & Milestone Management */}
        <div className="p-6 pb-4 rounded-2xl bg-card border border-border shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Project Progress</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {isEditing ? 'Define and manage project milestones' : 'Overall health and completion metrics'}
              </p>
            </div>
            {!isEditing && (
              <div className="px-4 py-2 bg-workspace-accent/10 rounded-xl border border-workspace-accent/20">
                <span className="text-sm font-medium text-workspace-accent">{project.progress}% Complete</span>
              </div>
            )}
          </div>

          {/* Overall Progress Bar */}
          <div className="mb-0">
            <div className="h-4 bg-muted rounded-full overflow-hidden shadow-inner border border-border/50">
              <div
                className="h-full rounded-full transition-all duration-1000 ease-out shadow-[0_0_15px_rgba(var(--workspace-accent-rgb),0.3)]"
                style={{
                  width: `${isEditing ? editProgress : project.progress}%`,
                  background: (isEditing ? editProgress : project.progress) === 100
                    ? 'linear-gradient(90deg, #10b981, #059669)'
                    : 'linear-gradient(90deg, var(--workspace-accent, #3b82f6), var(--workspace-accent-hover, #2563eb))',
                }}
              />
            </div>
          </div>

          {/* Edit Milestones section (only visible when editing) */}
          {isEditing && (
            <div className="mt-10 pt-8 border-t border-border space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-6 border-b border-border">
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase tracking-widest font-black text-muted-foreground">Project Name</label>
                  <Input value={editName} onChange={e => onNameChange(e.target.value)} className="bg-background/50" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase tracking-widest font-black text-muted-foreground">Status</label>
                  <Select value={editStatus} onValueChange={onStatusChange}>
                    <SelectTrigger className="bg-background/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-[10px] uppercase tracking-widest font-black text-muted-foreground block">Project Milestones</label>
                <div className="space-y-4">
                  {editStages.map((stage, idx) => (
                    <div key={stage.id} className="p-4 rounded-xl bg-muted/20 border border-border group animate-in fade-in slide-in-from-left-2 transition-all hover:border-border/80">
                      <div className="flex items-center gap-3 mb-4">
                        <button 
                          onClick={() => {
                            const updated = [...editStages];
                            updated[idx].is_completed = !updated[idx].is_completed;
                            onStagesChange(updated);
                          }}
                          className={`shrink-0 transition-colors ${stage.is_completed ? 'text-emerald-500' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                          {stage.is_completed ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                        </button>
                        <Input 
                          value={stage.name} 
                          onChange={(e) => {
                            const updated = [...editStages];
                            updated[idx].name = e.target.value;
                            onStagesChange(updated);
                          }}
                          className="border-0 bg-transparent h-auto p-0 text-base font-black focus-visible:ring-0 flex-1 placeholder:text-muted-foreground/30"
                          placeholder="Milestone name"
                        />
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all ml-auto">
                          <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg hover:bg-workspace-accent/10" onClick={() => moveStage(idx, 'up')} disabled={idx === 0}>
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="w-8 h-8 rounded-lg hover:bg-workspace-accent/10" onClick={() => moveStage(idx, 'down')} disabled={idx === editStages.length - 1}>
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                          <button onClick={() => removeStage(stage.id)} className="text-muted-foreground hover:text-destructive p-1.5 transition-all ml-1">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="pl-8">
                        <DateRangePicker 
                          value={{
                            from: stage.start_date ? parseISO(stage.start_date) : undefined,
                            to: stage.end_date ? parseISO(stage.end_date) : undefined
                          }}
                          onChange={(range) => {
                            const updated = [...editStages];
                            updated[idx].start_date = range.from ? format(range.from, 'yyyy-MM-dd') : undefined;
                            updated[idx].end_date = range.to ? format(range.to, 'yyyy-MM-dd') : undefined;
                            onStagesChange(updated);
                          }}
                          placeholder="Select milestone range"
                        />
                      </div>
                    </div>
                  ))}

                  <div className="p-6 rounded-2xl bg-workspace-accent/5 border border-dashed border-workspace-accent/20 space-y-6">
                    <div className="space-y-2">
                      <Label className="text-[10px] uppercase font-black text-workspace-accent px-1">Quick Add Milestone</Label>
                      <Input value={newStage} onChange={(e) => setNewStage(e.target.value)} placeholder="What's the next big goal?" className="bg-background h-12 text-sm font-bold rounded-xl border-border/50 shadow-sm focus-visible:ring-workspace-accent/20" />
                    </div>
                    <DateRangePicker 
                      value={{
                        from: newStageStart ? parseISO(newStageStart) : undefined,
                        to: newStageEnd ? parseISO(newStageEnd) : undefined
                      }}
                      onChange={(range) => {
                        setNewStageStart(range.from ? format(range.from, 'yyyy-MM-dd') : '');
                        setNewStageEnd(range.to ? format(range.to, 'yyyy-MM-dd') : '');
                      }}
                      placeholder="Select schedule"
                    />
                    <Button type="button" className="w-full h-11 font-black uppercase text-xs tracking-widest bg-workspace-accent hover:bg-workspace-accent/90 shadow-lg shadow-workspace-accent/20" onClick={addStage} disabled={!newStage.trim()}>
                      <Plus className="w-4 h-4 mr-2" /> Add to Timeline
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Card 2: Timeline Roadmap & Description */}
        {!isEditing && (
          <>
            <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden p-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 px-2">
                <div className="flex items-center gap-3">
                  <span className="text-xl font-semibold text-foreground tracking-tight">Project Timeline</span>
                  <div className="w-1.5 h-1.5 rounded-full bg-workspace-accent/40" />
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground mr-2">
                    <CalendarIcon className="w-4 h-4" />
                    <span>{new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                  </div>

                  <Select value={zoom} onValueChange={(v) => setZoom(v as any)}>
                    <SelectTrigger className="w-[115px] h-10 bg-muted/30 border-border/50 text-[13px] font-semibold rounded-xl">
                      <SelectValue placeholder="Select Zoom" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7d">7 Days</SelectItem>
                      <SelectItem value="1m">1 Month</SelectItem>
                      <SelectItem value="3m">3 Months</SelectItem>
                      <SelectItem value="6m">6 Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="relative min-h-[200px] border-t border-border/60">
                {(() => {
                  if (!project.stages || project.stages.length === 0) {
                    return (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <p className="text-sm text-muted-foreground italic font-medium">Add milestones to see the timeline.</p>
                      </div>
                    );
                  }

                  const projectStart = project.start_date ? new Date(project.start_date) : null;
                  const earliestMilestone = project.stages && project.stages.length > 0 
                    ? new Date(Math.min(...project.stages.flatMap(s => [
                        s.start_date ? new Date(s.start_date).getTime() : Infinity,
                        s.end_date ? new Date(s.end_date).getTime() : Infinity
                      ]).filter(t => t !== Infinity)))
                    : null;
                  
                  const originDate = projectStart || earliestMilestone || new Date();
                  originDate.setHours(0,0,0,0);
                  const startDate = new Date(originDate);
                  
                  const zoomConfigs = {
                    '7d': { days: 7, dayWidth: 150, showDays: true },
                    '1m': { days: 30, dayWidth: 40, showDays: true },
                    '3m': { days: 90, dayWidth: 15, showDays: false },
                    '6m': { days: 180, dayWidth: 8, showDays: false }
                  };
                  const config = zoomConfigs[zoom];
                  const rowHeight = 48;
                  const totalWidth = config.days * config.dayWidth;

                  return (
                    <div className="relative overflow-x-auto no-scrollbar pt-6 pb-2">
                      <div className="flex mb-4 sticky left-0 z-10" style={{ width: totalWidth }}>
                        {Array.from({ length: config.days }).map((_, i) => {
                          const d = new Date(startDate);
                          d.setDate(d.getDate() + i);
                          const isFirstDayOfMonth = d.getDate() === 1;
                          const isMonday = d.getDay() === 1;
                          const isToday = d.toDateString() === new Date().toDateString();
                          return (
                            <div key={i} className={`shrink-0 flex flex-col items-center justify-center border-r border-border/20 ${isMonday ? 'bg-workspace-accent/5' : ''} ${isToday ? 'bg-blue-50/10' : ''}`} style={{ width: config.dayWidth }}>
                              {(isFirstDayOfMonth || i === 0) && (
                                <span className="text-[9px] font-black text-workspace-accent uppercase absolute -top-5">
                                  {d.toLocaleDateString('en-US', { month: 'short' })}
                                </span>
                              )}
                              {config.showDays && (
                                <span className={`text-[10px] font-medium ${isToday ? 'text-blue-500' : 'text-muted-foreground'}`}>
                                  {d.getDate()}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <div className="relative" style={{ width: totalWidth, height: project.stages.length * rowHeight }}>
                        {(() => {
                          const today = new Date();
                          today.setHours(0,0,0,0);
                          const diff = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                          if (diff < 0 || diff >= config.days) return null;
                          return (
                            <div className="absolute top-0 bottom-0 w-0.5 bg-blue-500 z-30 pointer-events-none" style={{ left: diff * config.dayWidth }}>
                              <div className="absolute -top-1 -left-[5px] w-3 h-3 bg-blue-500 rounded-full border-2 border-background shadow-sm" />
                            </div>
                          );
                        })()}

                        {project.stages.map((stage, idx) => {
                          const eDate = stage.end_date ? new Date(stage.end_date) : null;
                          const sDate = stage.start_date ? new Date(stage.start_date) : eDate;
                          if (!eDate) return null;
                          const sDiff = (sDate!.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
                          const eDiff = (eDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
                          if (eDiff < 0 || sDiff > config.days) return null;
                          const start = Math.max(0, sDiff) * config.dayWidth;
                          const end = Math.min(config.days, eDiff) * config.dayWidth;
                          const barWidth = Math.max(config.dayWidth, end - start);
                          const colorClass = ['bg-blue-600/95', 'bg-amber-400/95', 'bg-orange-500/95', 'bg-indigo-600/95'][idx % 4];

                          return (
                            <div key={stage.id} className={`absolute h-10 rounded-lg flex items-center px-6 font-medium text-sm shadow-md border border-black/5 transition-all group z-10 ${stage.is_completed ? 'bg-emerald-500/20 text-emerald-800 line-through' : `${colorClass} text-white`}`} style={{ left: start, width: barWidth, top: idx * rowHeight + 4 }}>
                              <span className="truncate flex-1 tracking-tight">{stage.name}</span>
                              {!isClient && (
                                <button onClick={() => onToggleStage(stage.id)} className="ml-3 w-7 h-7 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 shadow-sm">
                                  {stage.is_completed ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {project.description && (
              <div className="p-8 rounded-2xl bg-card border border-border shadow-sm">
                <h3 className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-4">Project Description</h3>
                <p className="text-foreground leading-relaxed whitespace-pre-wrap">{project.description}</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Sidebar */}
      <div className="lg:col-span-3 space-y-6">
        {/* Timeline Countdown Card */}
        <div className="p-6 rounded-xl bg-card border border-border">
          <h3 className="text-sm font-semibold text-foreground mb-6 flex items-center gap-2">
            <Clock className="w-4 h-4 text-workspace-accent" /> Project Deadline
          </h3>
          
          {project.end_date ? (
            <div className="space-y-8">
              <div className="flex justify-center gap-4">
                {(() => {
                  const timeLeft = new Date(project.end_date).getTime() - Date.now();
                  const isOverdue = timeLeft < 0;
                  const absTime = Math.abs(timeLeft);
                  const days = Math.floor(absTime / (1000 * 60 * 60 * 24));
                  const hours = Math.floor((absTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                  const minutes = Math.floor((absTime % (1000 * 60 * 60)) / (1000 * 60));
                  
                  const numberClass = `text-4xl md:text-5xl font-semibold tracking-tight ${isOverdue ? 'text-red-500' : 'text-foreground'}`;
                  const labelClass = "text-xs uppercase tracking-widest text-muted-foreground font-semibold mt-2";
                  const colonClass = "text-3xl md:text-4xl font-medium text-muted-foreground/20 pt-1";
                  
                  return (
                    <>
                      <div className="text-center">
                        <div className={numberClass}>{days}</div>
                        <div className={labelClass}>Days</div>
                      </div>
                      <div className={colonClass}>:</div>
                      <div className="text-center">
                        <div className={numberClass}>{hours}</div>
                        <div className={labelClass}>Hrs</div>
                      </div>
                      <div className={colonClass}>:</div>
                      <div className="text-center">
                        <div className={numberClass}>{minutes}</div>
                        <div className={labelClass}>Min</div>
                      </div>
                    </>
                  );
                })()}
              </div>
              
            </div>
          ) : (
            <div className="py-8 text-center bg-muted/30 rounded-xl border border-dashed border-border">
              <p className="text-sm text-muted-foreground">No deadline set</p>
            </div>
          )}
        </div>

        {/* Project Members */}
        <div className="p-6 rounded-xl bg-card border border-border">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-workspace-accent" /> Project Members ({project.members?.length || 0})
          </h3>
          <div className="space-y-4">
            {project.members?.map(member => (
              <MemberRow key={member.id} member={member} showPermission={member.role === 'client'} />
            ))}
            {(!project.members || project.members.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-2">No members assigned</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Member Row
const MemberRow: React.FC<{ member: ProjectMember; showPermission?: boolean }> = ({ member, showPermission }) => {
  const name = member.profile
    ? `${member.profile.first_name || ''} ${member.profile.last_name || ''}`.trim() || member.profile.email?.split('@')[0]
    : 'Unknown';

  const roleIcon = member.role === 'owner'
    ? <Crown className="w-3.5 h-3.5 text-amber-500" />
    : member.role === 'manager'
      ? <Users className="w-3.5 h-3.5 text-blue-500" />
      : <UserCircle className="w-3.5 h-3.5 text-violet-500" />;

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-semibold text-foreground overflow-hidden">
          {member.profile?.avatar_url ? (
            <img src={member.profile.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            (name[0] || '?').toUpperCase()
          )}
        </div>
        <div>
          <div className="text-sm font-medium text-foreground flex items-center gap-1.5">
            {name} {roleIcon}
          </div>
          <div className="text-xs text-muted-foreground">{member.profile?.email}</div>
        </div>
      </div>
      {showPermission && (
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          member.permission === 'edit'
            ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
            : 'bg-gray-100 dark:bg-gray-500/20 text-gray-600 dark:text-gray-400'
        }`}>
          {member.permission === 'edit' ? (
            <span className="flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</span>
          ) : (
            <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> View</span>
          )}
        </span>
      )}
    </div>
  );
};

// Placeholder Tab for unimplemented tabs
const PlaceholderTab: React.FC<{ icon: React.ReactNode; title: string; description: string; action?: React.ReactNode }> = ({ icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-20">
    <div className="w-24 h-24 rounded-2xl bg-workspace-accent/10 flex items-center justify-center mb-6">
      {icon}
    </div>
    <h2 className="text-xl font-bold text-foreground mb-2">{title}</h2>
    <p className="text-muted-foreground text-center max-w-md">{description}</p>
    {action}
  </div>
);

export default ProjectDetail;
